import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Box, Typography, Button, Container, Grid, Paper, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState } from 'react';

const StyledFeatureCard = styled(motion.div)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '24px',
  padding: '2rem',
  height: '100%',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
}));

const StyledIcon = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
}));

const CopyableCodeBlock = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <Paper sx={{ 
      bgcolor: 'rgba(30, 41, 59, 0.5)', 
      color: '#60A5FA', 
      p: 2, 
      my: 2, 
      fontFamily: 'monospace', 
      fontSize: 15, 
      overflowX: 'auto', 
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      position: 'relative' 
    }}>
      <pre style={{ margin: 0, color: '#60A5FA' }}>{code}</pre>
      <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            color: copied ? '#FBBF24' : '#60A5FA',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Documentation = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const simpleExamples = [
    {
      title: 'Basic LLM Response with Ads',
      code: `# FastAPI endpoint\n@app.post("/chat/")\nasync def chat_endpoint(request: Request, chat: dict):\n    llm_output = generate_llm_response(chat["prompt"])\n    return {\n        "response": llm_output,\n        "ads": request.state.selected_ads\n    }`,
      response: `# Sample response\n{\n  "response": "Here is your answer!",\n  "ads": [\n    {\n      "id": 1,\n      "title": "Try Acme AI Tools!",\n      "description": "Boost productivity with Acme's AI suite.",\n      "category": "productivity",\n      "url": "https://acme.ai"\n    }\n  ]\n}`
    },
    {
      title: 'Embedding Ads in HTML',
      code: `# FastAPI endpoint\n@app.post("/chat/")\nasync def chat_endpoint(request: Request, chat: dict):\n    llm_output = generate_llm_response(chat["prompt"])\n    ads_html = "".join([\n        f"<div class='ad'>{ad['title']}: {ad['description']}</div>"\n        for ad in request.state.selected_ads\n    ])\n    return {\n        "response": f"{llm_output}<hr>{ads_html}"\n    }`,
      response: `# Sample response\n{\n  "response": "Here is your answer!<hr><div class='ad'>Try Acme AI Tools!: Boost productivity with Acme's AI suite.</div>"\n}`
    },
    {
      title: 'Custom Placement in JSON',
      code: `# FastAPI endpoint\n@app.post("/chat/")\nasync def chat_endpoint(request: Request, chat: dict):\n    llm_output = generate_llm_response(chat["prompt"])\n    return {\n        "main_content": llm_output,\n        "ads": request.state.selected_ads\n    }`,
      response: `# Sample response\n{\n  "main_content": "Here is your answer!",\n  "ads": [\n    {\n      "id": 2,\n      "title": "Order Food Instantly!",\n      "description": "Get 20% off your first order.",\n      "category": "food",\n      "url": "https://foodfast.com"\n    }\n  ]\n}`
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
      color: 'white',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none',
      }
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ paddingTop: '8rem', paddingBottom: '4rem' }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '1.5rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Integration Guide
          </Typography>
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              color: '#94A3B8',
              maxWidth: '800px',
              margin: '0 auto',
              marginBottom: '3rem',
            }}
          >
            Get started with Reasonic integration in minutes
          </Typography>
        </motion.div>
      </Container>

      {/* Quick Start Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">1️⃣</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Register
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Sign up as a publisher and get your unique Publisher ID
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">2️⃣</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Install
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Install the Reasonic SDK with a single command
                </Typography>
                <CopyableCodeBlock code="pip install llm_ads" />
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledFeatureCard variants={fadeInUp}>
                <StyledIcon>
                  <Typography variant="h4">3️⃣</Typography>
                </StyledIcon>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                  Integrate
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Add the middleware to your FastAPI app
                </Typography>
                <CopyableCodeBlock code={`from fastapi import FastAPI, Request
from llm_ads import AdServingMiddleware, AdServingConfig

app = FastAPI()

app.add_middleware(
    AdServingMiddleware,
    config=AdServingConfig(
        publisher_id="YOUR_PUBLISHER_ID"
    )
)`} />
              </StyledFeatureCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Examples Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Integration Examples
          </Typography>
          {simpleExamples.map((ex, idx) => (
            <Box key={idx} sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: '#FBBF24'
              }}>
                {ex.title}
              </Typography>
              <CopyableCodeBlock code={ex.code} />
              <CopyableCodeBlock code={ex.response} />
            </Box>
          ))}
        </motion.div>
      </Container>

      {/* FAQ Section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '3rem',
              background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <StyledFeatureCard>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#FBBF24' }}>
                  Do I need to set up a database?
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  No. All ad serving and analytics are managed by Reasonic.
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledFeatureCard>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#FBBF24' }}>
                  How do I get my publisher ID?
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  Register on the Sign Up page. Your ID will be shown after registration.
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledFeatureCard>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#FBBF24' }}>
                  How do I see my ad performance?
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Use the Analytics Dashboard in Reasonic to track impressions, clicks, and revenue.
                </Typography>
              </StyledFeatureCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledFeatureCard>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#FBBF24' }}>
                  What's the revenue share?
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                  We offer up to 85% revenue share with our competitive payout structure.
                </Typography>
              </StyledFeatureCard>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Support Section */}
      <Box sx={{ 
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 1) 100%)',
        padding: '6rem 0',
        position: 'relative',
        zIndex: 1,
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center' }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                marginBottom: '1.5rem',
                background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Need Help?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#94A3B8',
                marginBottom: '3rem',
              }}
            >
              Our support team is here to help you with integration
            </Typography>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  padding: '1rem 2.5rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  },
                }}
              >
                Contact Support
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Documentation; 